import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { CalendarClock, Eye, FileText, Newspaper, Play, PenLine, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { get, imgUrl } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { Panel, RunBadge, StatusBadge, cadenceText } from "@/admin/ui";
import { GenerateDialog } from "@/admin/GenerateDialog";
import { RunDialog } from "@/admin/RunProgress";
import { useAutomationActions } from "@/admin/hooks";

const greeting = () => {
  const h = new Date().getHours();
  return h < 13 ? "Buenos días" : h < 20 ? "Buenas tardes" : "Buenas noches";
};

const Metric = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="bg-card border border-border p-5" data-testid={testid}>
    <div className="flex items-center justify-between text-muted-foreground"><span className="kicker text-[10px]">{label}</span><Icon className="h-4 w-4" /></div>
    <p className="font-display font-black text-4xl mt-3 tabular-nums">{value ?? "—"}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const AutomationRow = ({ a, actions }) => (
  <div className="py-4 border-b border-border last:border-0" data-testid={`dash-automation-${a.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold truncate">{a.name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{cadenceText(a)}</p>
        <p className="text-xs mt-1">{a.enabled ? <span className="text-primary">Próxima {timeAgo(a.next_run_at)}</span> : <span className="text-muted-foreground">En pausa</span>}</p>
      </div>
      <Switch checked={a.enabled} onCheckedChange={() => actions.toggle.mutate(a)} data-testid={`dash-automation-toggle-${a.id}`} />
    </div>
    <Button size="sm" variant="outline" className="mt-3" onClick={() => actions.run.mutate(a)} disabled={actions.run.isPending} data-testid={`dash-automation-run-${a.id}`}>
      <Play /> Ejecutar ahora
    </Button>
  </div>
);

export default function Dashboard() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const actions = useAutomationActions();
  const { data } = useQuery({ queryKey: ["stats"], queryFn: () => get("/admin/stats"), refetchInterval: 15000 });
  const chart = (data?.per_day || []).map((d) => ({ ...d, label: d.date.slice(8, 10) + "/" + d.date.slice(5, 7) }));

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-6 mb-10">
        <div>
          <p className="kicker text-primary">Redacción</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-1" data-testid="dashboard-greeting">{greeting()}, {user.name?.split(" ")[0]}.</h1>
          <p className="mt-2 text-muted-foreground">Esto es lo que está pasando hoy en tu periódico.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="lg" onClick={() => navigate("/admin/noticias/nueva")} data-testid="dashboard-new-article-btn"><PenLine /> Escribir noticia</Button>
          <GenerateDialog trigger={<Button size="lg" variant="outline" className="border-foreground" data-testid="dashboard-generate-btn"><Sparkles /> Generar con IA</Button>} />
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Metric icon={Newspaper} label="Publicadas" value={data?.published} hint="en la web" testid="metric-published" />
        <Metric icon={CalendarClock} label="Programadas" value={data?.scheduled} hint="saldrán solas" testid="metric-scheduled" />
        <Metric icon={FileText} label="Borradores" value={data?.draft} hint="pendientes de revisar" testid="metric-drafts" />
        <Metric icon={Eye} label="Lecturas" value={data?.views} hint="totales" testid="metric-views" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Panel title="Publicaciones · últimos 14 días" className="lg:col-span-2" testid="dashboard-chart">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} formatter={(v) => [v, "Noticias"]} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Automatizaciones" description={`${data?.automations_active ?? 0} activas`} actions={<Link to="/admin/automatizaciones" className="kicker text-[10px] text-primary" data-testid="dashboard-automations-link">Gestionar</Link>} testid="dashboard-automations">
          {(data?.automations || []).map((a) => <AutomationRow key={a.id} a={a} actions={actions} />)}
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Panel title="Últimas noticias" className="lg:col-span-2" actions={<Link to="/admin/noticias" className="kicker text-[10px] text-primary" data-testid="dashboard-articles-link">Ver todas</Link>} testid="dashboard-recent-articles">
          {(data?.recent_articles || []).map((a) => (
            <Link key={a.id} to={`/admin/noticias/${a.id}`} className="flex items-center gap-4 py-3 border-b border-border last:border-0 group" data-testid={`dashboard-article-${a.id}`}>
              {a.image_url ? <img src={imgUrl(a.image_url)} alt="" className="h-12 w-16 object-cover shrink-0" /> : <div className="h-12 w-16 cover-fallback shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate group-hover:text-primary">{a.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{a.category_name} · {timeAgo(a.updated_at)}</p>
              </div>
              <StatusBadge status={a.status} />
            </Link>
          ))}
          {data && !data.recent_articles.length && <p className="text-sm text-muted-foreground">Aún no hay noticias.</p>}
        </Panel>
        <Panel title="Actividad reciente" actions={<Link to="/admin/actividad" className="kicker text-[10px] text-primary" data-testid="dashboard-activity-link">Ver todo</Link>} testid="dashboard-activity">
          {(data?.recent_runs || []).map((r) => (
            <div key={r.id} className="py-3 border-b border-border last:border-0">
              <div className="flex items-center justify-between gap-2"><p className="text-sm font-medium truncate">{r.label}</p><RunBadge status={r.status} /></div>
              <p className="text-xs text-muted-foreground mt-1">{r.message || "En curso"} · {timeAgo(r.started_at)}</p>
            </div>
          ))}
          {data && !data.recent_runs.length && <p className="text-sm text-muted-foreground">Sin actividad todavía.</p>}
        </Panel>
      </div>
      <RunDialog runId={actions.runId} onClose={() => actions.setRunId(null)} />
    </div>
  );
}
